import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import {
  Alert,
  Box,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import SecurityIcon from '@mui/icons-material/Security';
import PageHeader from '../../components/common/PageHeader';
import api from '../../api/axiosInstance';

const normalizeSerial = (value) => String(value || '')
  .trim()
  .toUpperCase()
  .replace(/[^A-Z0-9]/g, '');

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getLevelSerial = (device = {}) => device.serial_number || device.serialNumber || device.serial || device.hardware?.serialNumber || '';
const getEsetSerial = (device = {}) => device.hardware?.[0]?.serialNumber || device.serialNumber || device.serial_number || '';

const getLevelName = (device = {}) => device.name || device.hostname || device.computer_name || device.device_name || '';
const getEsetName = (device = {}) => device.name || device.hostname || '';

const getLevelOrganizationName = (device = {}) => device.matchedOrganizationName || device.matchedOrganization?.name || device.organizationName || '';
const getEsetOrganizationName = (device = {}) => device.organizationDetails?.name || device.organizationName || '';

const getLevelOs = (device = {}) => device.operating_system?.name || device.os?.name || device.operatingSystem || '';
const getLevelOsVersion = (device = {}) => device.operating_system?.version || device.os?.version || '';
const getLevelGroup = (device = {}) => device.matchedLevelGroupPath || device.levelGroupName || device.group?.name || '';

const componentNames = (device = {}) => Array.isArray(device.components)
  ? device.components.map((component) => component.name).filter(Boolean).join(', ')
  : '';

function useClientOrganizationNames() {
  const { data: organizations = [] } = useSelector((state) => state.organizations.organizations);
  return useMemo(
    () => organizations.map((organization) => organization.name).filter(Boolean),
    [organizations],
  );
}

export default function ClientDevices() {
  const organizationNames = useClientOrganizationNames();
  const { data: esetDevices = [], loading: esetLoading, error: esetError } = useSelector((state) => state.eset.devices);
  const [levelDevices, setLevelDevices] = useState([]);
  const [levelLoading, setLevelLoading] = useState(true);
  const [levelError, setLevelError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLevelLoading(true);
    setLevelError('');
    api.get('/api/level/devices?includeDeleted=false')
      .then(({ data }) => {
        if (mounted) setLevelDevices(data.devices || []);
      })
      .catch((error) => {
        if (mounted) setLevelError(error.response?.data?.message || error.response?.data?.error || 'Impossible de charger les appareils Level.io.');
      })
      .finally(() => {
        if (mounted) setLevelLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const clientName = organizationNames[0] || 'Client';

  const filteredLevelDevices = useMemo(() => {
    const hasOrganizationName = levelDevices.some((device) => getLevelOrganizationName(device));
    if (!organizationNames.length || !hasOrganizationName) return levelDevices;
    return levelDevices.filter((device) => organizationNames.includes(getLevelOrganizationName(device)));
  }, [levelDevices, organizationNames]);

  const filteredEsetDevices = useMemo(() => {
    const activeDevices = esetDevices.filter((device) => !device.deleted);
    const hasOrganizationName = activeDevices.some((device) => getEsetOrganizationName(device));
    if (!organizationNames.length || !hasOrganizationName) return activeDevices;
    return activeDevices.filter((device) => organizationNames.includes(getEsetOrganizationName(device)));
  }, [esetDevices, organizationNames]);

  const rows = useMemo(() => {
    const bySerial = new Map();
    const addRow = (key, payload) => {
      if (!bySerial.has(key)) bySerial.set(key, { serialKey: key, level: null, eset: null });
      Object.assign(bySerial.get(key), payload);
    };

    filteredLevelDevices.forEach((device, index) => {
      const serial = normalizeSerial(getLevelSerial(device));
      addRow(serial || `level-${device.id || device.device_id || index}`, { level: device });
    });

    filteredEsetDevices.forEach((device, index) => {
      const serial = normalizeSerial(getEsetSerial(device));
      addRow(serial || `eset-${device._id || device.id || index}`, { eset: device });
    });

    return Array.from(bySerial.values()).map((row, index) => {
      const level = row.level || {};
      const eset = row.eset || {};
      const levelSerial = getLevelSerial(level);
      const esetSerial = getEsetSerial(eset);
      return {
        id: row.serialKey || index,
        serialNumber: levelSerial || esetSerial || '',
        name: getLevelName(level) || getEsetName(eset) || '',
        hostname: level.hostname || eset.hostname || '',
        manufacturer: level.manufacturer || eset.hardware?.[0]?.manufacturer || '',
        model: level.model || eset.hardware?.[0]?.model || '',
        client: getLevelOrganizationName(level) || getEsetOrganizationName(eset),
        levelGroup: getLevelGroup(level),
        levelStatus: level.status || '',
        levelLastSeen: formatDate(level.last_seen_at || level.last_check_in || level.updated_at),
        levelOs: [getLevelOs(level), getLevelOsVersion(level)].filter(Boolean).join(' '),
        esetTenant: eset.tenantName || '',
        esetLastSync: formatDate(eset.lastSync),
        ipLan: eset.ipLAN || level.ip_address || '',
        ipWan: eset.ipWAN || level.public_ip_address || '',
        esetComponents: componentNames(eset),
        inLevel: !!row.level,
        inEset: !!row.eset,
      };
    }).sort((a, b) => String(a.name || a.serialNumber).localeCompare(String(b.name || b.serialNumber)));
  }, [filteredLevelDevices, filteredEsetDevices]);

  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Nom',
      size: 220,
      filterVariant: 'autocomplete',
      Cell: ({ renderedCellValue }) => <Typography sx={{ fontWeight: 700 }}>{renderedCellValue}</Typography>,
    },
    {
      accessorKey: 'serialNumber',
      header: 'N° série',
      size: 180,
      filterVariant: 'autocomplete',
      Cell: ({ renderedCellValue }) => renderedCellValue || <Typography variant="body2" color="text.secondary">Non renseigné</Typography>,
    },
    {
      accessorFn: (row) => [row.inLevel ? 'Level.io' : '', row.inEset ? 'ESET' : ''].filter(Boolean).join(' '),
      id: 'sources',
      header: 'Sources',
      size: 180,
      filterVariant: 'multi-select',
      Cell: ({ row }) => (
        <Stack direction="row" spacing={0.75}>
          {row.original.inLevel ? <Chip size="small" icon={<ComputerIcon />} label="Level.io" color="primary" variant="outlined" /> : null}
          {row.original.inEset ? <Chip size="small" icon={<SecurityIcon />} label="ESET" color="success" variant="outlined" /> : null}
        </Stack>
      ),
    },
    { accessorKey: 'hostname', header: 'Hostname', size: 180, filterVariant: 'autocomplete' },
    { accessorKey: 'manufacturer', header: 'Fabricant', size: 160, filterVariant: 'autocomplete' },
    { accessorKey: 'model', header: 'Modèle', size: 180, filterVariant: 'autocomplete' },
    { accessorKey: 'levelGroup', header: 'Dossier Level.io', size: 220, filterVariant: 'autocomplete' },
    { accessorKey: 'levelStatus', header: 'Statut Level', size: 140, filterVariant: 'autocomplete' },
    { accessorKey: 'levelLastSeen', header: 'Vu par Level', size: 170 },
    { accessorKey: 'levelOs', header: 'OS Level', size: 220, filterVariant: 'autocomplete' },
    { accessorKey: 'esetTenant', header: 'Tenant ESET', size: 180, filterVariant: 'autocomplete' },
    { accessorKey: 'esetLastSync', header: 'Synchro ESET', size: 170 },
    { accessorKey: 'ipLan', header: 'IP LAN', size: 140 },
    { accessorKey: 'ipWan', header: 'IP WAN', size: 140 },
    { accessorKey: 'esetComponents', header: 'Composants ESET', size: 260 },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization: MRT_Localization_FR,
    enableColumnFilterModes: true,
    enableColumnFilters: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    enableColumnResizing: true,
    enableFacetedValues: true,
    enableGlobalFilter: true,
    initialState: {
      density: 'compact',
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 50 },
      columnPinning: { left: ['name', 'serialNumber', 'sources'] },
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
      placeholder: 'Rechercher',
    },
    muiPaginationProps: {
      rowsPerPageOptions: [25, 50, 100],
      shape: 'rounded',
      variant: 'outlined',
    },
    paginationDisplayMode: 'pages',
    positionGlobalFilter: 'right',
    state: { isLoading: levelLoading || esetLoading },
  });

  const loading = levelLoading || esetLoading;

  return (
    <Box>
      <PageHeader section={clientName} title="Vos appareils" sx={{ my: 1, mb: 3 }} />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip label={`${rows.length} appareil(s)`} />
        <Chip color="primary" variant="outlined" label={`${rows.filter((row) => row.inLevel).length} Level.io`} />
        <Chip color="success" variant="outlined" label={`${rows.filter((row) => row.inEset).length} ESET`} />
        <Chip color="warning" variant="outlined" label={`${rows.filter((row) => row.inLevel && !row.inEset).length} sans ESET`} />
      </Stack>

      {levelError ? <Alert severity="error" sx={{ mb: 2 }}>{levelError}</Alert> : null}
      {esetError ? <Alert severity="error" sx={{ mb: 2 }}>Erreur de chargement des appareils ESET.</Alert> : null}

      {loading && !rows.length ? (
        <Skeleton variant="rectangular" width="100%" height={520} />
      ) : rows.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="h6" color="text.secondary">Aucun appareil trouvé</Typography>
        </Box>
      ) : (
        <MaterialReactTable table={table} />
      )}
    </Box>
  );
}
